import React from "react";
import { useTranslation } from "react-i18next";
import { BillingPageState } from "@/components/pages/billing-settings/components/BillingPageState";
import { useLegalPage } from "@/hooks/useLegalPages";
import { LegalPageType } from "@/types/legal-page";

interface LegalPageViewProps {
  type: LegalPageType;
  titleKey: string;
}

export const LegalPageView: React.FC<LegalPageViewProps> = ({
  type,
  titleKey,
}) => {
  const { t, i18n } = useTranslation();
  const language = (i18n.resolvedLanguage || i18n.language || "en").split(
    "-",
  )[0];
  const { data: legalPage, isLoading, isError } = useLegalPage(type, language);

  if (isLoading) {
    return <BillingPageState text={t("layout.legalPages.loading")} />;
  }

  if (isError || !legalPage) {
    return (
      <BillingPageState text={t("layout.legalPages.loadError")} tone="error" />
    );
  }

  return (
    <div className="px-4 py-10 sm:px-6 lg:px-8">
      <div className="flex w-full flex-col gap-6">
        <div className="space-y-2 border-b border-zinc-200 pb-6">
          <h1 className="text-3xl font-semibold text-zinc-900">
            {t(titleKey)}
          </h1>
          <p className="text-sm text-zinc-500 *:*:w-full">
            {t("layout.legalPages.lastUpdated", {
              value: new Intl.DateTimeFormat(undefined, {
                dateStyle: "medium",
                timeStyle: "short",
              }).format(new Date(legalPage.updatedAt)),
            })}
          </p>
        </div>

        <div
          className="prose prose-zinc max-w-none prose-headings:font-semibold prose-a:text-zinc-900 prose-img:rounded-2xl"
          dangerouslySetInnerHTML={{ __html: legalPage.content }}
        />
      </div>
    </div>
  );
};
